import { produce } from 'immer';

import { getConsentData } from '../agreementsConfig';
import { MODE } from '../config';
import api from '../services/api';
import { eventBus } from '../services/eventBus';
import actions from './index';
import { createReducerFor } from './utils';

const TYPE = 'CONSENTS';

const mergeConsentProfileSlice = (previous = {}, incoming = {}) => {
    const out = { ...previous };
    Object.keys(incoming).forEach((k) => {
        const prev = previous[k];
        const inc = incoming[k];
        if (inc !== null && typeof inc === 'object' && !Array.isArray(inc)) {
            out[k] = { ...(prev && typeof prev === 'object' && !Array.isArray(prev) ? prev : {}), ...inc };
        } else {
            out[k] = inc;
        }
    });
    return out;
};

const normalizeProfileId = (profileId) => `${profileId ?? ''}`;

const getProfileCardsProviderCodes = (state, profileId, mainProfileId) => {
    const userInfo = state?.user?.info ?? {};
    const isMainProfile = normalizeProfileId(profileId) === normalizeProfileId(mainProfileId);

    if (isMainProfile) {
        const mainCards = userInfo?.me?.cards ?? [];
        return mainCards
            ?.map?.(card => card?.providerCode)
            ?.filter?.(Boolean) ?? [];
    }

    const relatives = userInfo?.relatives ?? [];
    const relative = relatives?.find?.((entry) => normalizeProfileId(entry?.profileId) === normalizeProfileId(profileId));
    const relativeCards = relative?.cards ?? relative?.profile?.cards ?? [];

    return relativeCards
        ?.map?.(card => card?.providerCode)
        ?.filter?.(Boolean) ?? [];
};

/** Keep clinic scope for merge when PATCH carries an explicit revoke (false); otherwise merge would miss the key and old true would stay in Redux. */
const clinicConsentScopeHasTrue = (scope = {}) =>
    Object.values(scope ?? {}).some((v) => v === true);

const clinicConsentScopeHasExplicitFalse = (scope = {}) =>
    Object.values(scope ?? {}).some((v) => v === false);

/**
 * Without a card: keep clinic only if scope has explicit true, or explicit false (revoke must reach mergeConsentProfileSlice).
 * Otherwise only-null / empty scopes are dropped.
 */
const filterClinicConsentsByCards = (consentList, allowedProviders = []) => {
    if (!consentList || typeof consentList !== 'object') {
        return consentList;
    }

    const allowedSet = new Set(allowedProviders);

    return Object.entries(consentList)
        .reduce((acc, [ providerCode, consentValue ]) => {
            if (providerCode === 'main') {
                acc[providerCode] = consentValue;
                return acc;
            }
            if (
                allowedSet.has(providerCode)
                || clinicConsentScopeHasTrue(consentValue)
                || clinicConsentScopeHasExplicitFalse(consentValue)
            ) {
                acc[providerCode] = consentValue;
            }
            return acc;
        }, {});
};

export const initialState = {
    user: {},
    family: {},
    isLoading: false,
    providerErrors: [],
    providers: {},
};

export const setProviders = providers => dispatch => {
    dispatch({
        type: TYPE,
        payload: { providers },
    });
};

export const setConsents = data => dispatch => {
    dispatch({
        type: TYPE,
        payload: data,
    });
};


export const addProviderError = (provider, changeState = true) => (dispatch, getState) => {
    const {
        providerErrors,
        family,
        user,
    } = getState?.()?.consents ?? {};
    const prv = Array.isArray(provider) ? provider : [ provider ];
    let newProviders = [];
    let newUser = {};
    let newFamily = {};
    try {
        newProviders = Array.from(new Set([ ...providerErrors, ...prv ]));
        newUser = JSON.parse(JSON.stringify(user));
        newFamily = JSON.parse(JSON.stringify(family));
    } catch (e) {
        //
    }

    if (changeState) {
        newProviders.forEach(provider => {
            if (newUser[provider]) {
                Object.entries(newUser[provider])
                    .forEach(([ key, value ]) => {
                        if (value === null) {
                            newUser[provider][key] = false;
                        }
                    });
            }
        });

        Object.keys(newFamily)
            .forEach((profileId) => {
                newProviders.forEach(provider => {
                    const profile = newFamily[profileId];
                    if (profile?.[provider]) {
                        Object.entries(profile[provider])
                            .forEach(([ key, value ]) => {
                                if (value === null) {
                                    newFamily[profileId][provider][key] = false;
                                }
                            });
                    }
                });
            });

        dispatch({
            type: TYPE,
            payload: {
                providerErrors: newProviders,
                user: newUser,
                family: newFamily,
            },
        });
    } else {
        dispatch({
            type: TYPE,
            payload: { providerErrors: newProviders },
        });
    }

};

export const removeProviderError = provider => (dispatch, getState) => dispatch({
    type: TYPE,
    payload: { providerErrors: (getState?.()?.consents?.providerErrors ?? [])?.filter?.(p => p !== provider) },
});

export const clearProviderError = () => dispatch => dispatch({
    type: TYPE,
    payload: { providerErrors: [] },
});

export const changeConsent =
    ({
        providerCode: pc,
        branchId,
        profileId,
        consent,
        onComplete,
        onError,
        onCancel,
        disableTimer = false,
        loadingDelay = 0,
        checkConsents = true,
    }) => (dispatch, getState) => {
        console.log('SET consents called', {
            providerCode: pc,
            branchId,
            profileId,
            consent,
        });
        try {
            let providerCode = pc;
            if (!pc && branchId) {
                providerCode = getState?.()?.providerCodes?.[branchId]?.providerCode;
            }
            const consents = getState?.()?.consents;

            if (!profileId) {
                onCancel?.({
                    type: 'error',
                    message: Object.R('error.NoProfileIdProvided'),
                });
                return;
            }
            if (typeof consent !== 'object' || !Object.keys(consent)?.length) {
                onCancel?.({
                    type: 'info',
                    message: Object.R('error.notningToChange'),
                    consent,
                });
                return;
            }

            if (!disableTimer) {
                Object.disableUpdateProfileTimer = Date.now();
            }
            const currentConsentList = Object.keys(consent)
                ?.map((key) => getConsentData(key, providerCode)?.textWithPrefix);
            dispatch({
                type: TYPE,
                payload: { isLoading: true },
            });
            if (MODE === 'qa' && consents?.failedUpdateMode) {
                throw new Error(`Ошибка установки консента(ов):\n\n ${currentConsentList?.join(',\n\n ')}`);
            }
            api.setConsentsFromObject({
                profileId,
                consent,
                providerCode,
            })
                .then((res) => {
                    console.log('!!!Result of set consent', res);
                    const rawConsentList = (res?.consents ?? [])?.reduce?.((p, c) => {
                        const res = { ...p };
                        const provider = c?.providerCode ?? 'main';
                        if (!res?.[provider]) {
                            res[provider] = {};
                        }
                        res[provider][c?.type] = c?.provided;
                        return res;
                    }, {});
                    const cards = res?.cards ?? [];
                    const providersWithCards = getProfileCardsProviderCodes(getState?.(), profileId, consents?.profileId);
                    const consentList = filterClinicConsentsByCards(rawConsentList, providersWithCards);
                    const stateNow = getState?.()?.consents ?? {};
                    actions.addCardsToProfile(profileId, cards);
                    if (profileId === consents?.profileId) {
                        const mergedUser = mergeConsentProfileSlice(stateNow?.user ?? {}, consentList);
                        console.log('!!!set main user consents', mergedUser);
                        if (loadingDelay === 0) {
                            dispatch({
                                type: TYPE,
                                payload: {
                                    user: mergedUser,
                                    isLoading: false,
                                },
                            });
                            if (checkConsents) {
                                setTimeout(() => eventBus.emit('checkUserConsents', {from: 'changeConsent',
                                    codePosition: 'actions consent/changeConsent, result after change 263, without delay'}), 0);
                            }
                        } else {
                            dispatch({
                                type: TYPE,
                                payload: {
                                    user: mergedUser,
                                },
                            });
                            setTimeout(() => {
                                dispatch({
                                    type: TYPE,
                                    payload: {
                                        isLoading: false,
                                    },
                                });
                                eventBus.emit('checkUserConsents', { from: 'changeConsents',
                                    codePosition: 'actions consent/changeConsent, result after change 281, with delay'});
                            }, loadingDelay);
                        }
                        const familySnapshot = JSON.parse(JSON.stringify(stateNow?.family ?? {}));
                        Object.globalConsentHandler?.({
                            status: 'complete',
                            data: {
                                family: familySnapshot,
                                user: mergedUser,
                                profileId: consents?.profileId,
                            },
                        });
                        onComplete?.({
                            family: familySnapshot,
                            user: mergedUser,
                            profileId: consents?.profileId,
                        });
                    } else {
                        const familyMerged = JSON.parse(JSON.stringify(stateNow?.family ?? {}));
                        const prevProf = familyMerged[profileId] ?? {};
                        familyMerged[profileId] = mergeConsentProfileSlice(prevProf, consentList);
                        console.log(`!!!set family user consents ${profileId}`, {
                            family: familyMerged,
                            consentList,
                        });
                        if (loadingDelay === 0) {
                            dispatch({
                                type: TYPE,
                                payload: {
                                    family: familyMerged,
                                    isLoading: false,
                                },
                            });
                            setTimeout(() => eventBus.emit('checkUserConsents', {from: 'changeConsents',
                                codePosition: 'actions consent/changeConsent, result after change 315, childProfile'}), 0);
                        } else {
                            dispatch({
                                type: TYPE,
                                payload: {
                                    family: familyMerged,
                                },
                            });
                            setTimeout(() => {
                                dispatch({
                                    type: TYPE,
                                    payload: {
                                        isLoading: false,
                                    },
                                });
                                eventBus.emit('checkUserConsents', {from: 'changeConsents',
                                    codePosition: 'actions consent/changeConsent, result after change 331 delay childProfile'});
                            });
                        }
                        Object.globalConsentHandler?.({
                            status: 'complete',
                            data: {
                                family: familyMerged,
                                user: familyMerged[profileId],
                                profileId: consents?.profileId,
                            },
                        });
                        onComplete?.({
                            family: familyMerged,
                            user: familyMerged[profileId],
                            profileId: consents?.profileId,
                        });
                    }
                })
                .catch((e) => {
                    dispatch({
                        type: TYPE,
                        payload: {
                            isLoading: false,
                        },
                    });
                    Object.globalConsentHandler?.({
                        status: 'error',
                        data: {
                            profileId: consents?.profileId,
                            error: e,
                        },
                    });
                    onError?.(e, providerCode, consent);
                });
        } catch (e) {
            console.log('Error change consent', e);
            Object.globalConsentHandler?.({
                status: 'error',
                data: { error: e },
            });
            dispatch({
                type: TYPE,
                payload: {
                    isLoading: false,
                },
            });
            onError?.(e);
        }
    };


const updateConsentsService = async ({profileId, mainConsents, clinicsConsents }) => {
    const mainConsentsResult = [];
    const clinicsConsentsResult = [];
    let lastResult;
    try {
        if (mainConsents?.length) {
            const result = await api.setConsents({
                profileId,
                consents: mainConsents
            });
            // const result = {};
            lastResult = result;
            mainConsentsResult.push({
                type: 'main',
                status: 'ok',
                result
            });
        }
    } catch (e) {
        mainConsentsResult.push({type: 'main', status: 'error', result: e});
    }
    for (let consentIndex in clinicsConsents) {
        const c = clinicsConsents[consentIndex];
        if (!c || !c?.length) {
            continue;
        }
        const consent = Array.isArray(c) ? c : [ c ];
        try {
            const result = await api.setConsents({ profileId, consents: consent });
            lastResult = result;
            clinicsConsentsResult.push({type: 'clinic', status: 'ok', result, providerCode: consentIndex, request: consent });
        } catch (e) {
            clinicsConsentsResult.push({type: 'clinic', status: 'error', result: e, providerCode: consentIndex, request: consent });
        }
    }
    return { results: [...mainConsentsResult, ...clinicsConsentsResult], info: lastResult };
}

/**
 * Batch update consents. Use with caution, as it will trigger multiple updates in a short time, which may lead to unexpected behavior. Make sure to handle onComplete and onError callbacks properly to avoid issues.
 * @param profileId
 * @param consents - [{type: string, provided: boolean, providerCode: string}]
 * @param onComplete
 * @param onError
 * @returns {(function(*, *))|*}
 */
export const batchChangeConsent = ({profileId, consents = [], onComplete, onError} = {}) => (dispatch, getState) => {
    const consentsState = getState?.()?.consents ?? { };
    try {
        const checkedConsents = consents?.filter?.(c => c?.type && Object.hasOwn?.(c,'provided'));
        const mainConsents = checkedConsents?.filter?.(c => !c?.providerCode);
        const clinicsConsents = checkedConsents?.filter?.(c => c?.providerCode);
        const clinics = clinicsConsents?.reduce?.((p, c) => {
            const current = p?.[c?.providerCode] ?? [];
            current.push(c);
            return {...p, [c?.providerCode]: current};

        }, {});
        // console.log('batchConsentUpdate', {profileId, consents, mainConsents, clinicsConsents, checkedConsents, clinics});
        dispatch({type: TYPE, payload: { isLoading: true }});
        updateConsentsService({profileId, mainConsents, clinicsConsents: clinics})
            .then((res) => {
                if (res?.info) {
                    const rawConsentList = (res?.info?.consents ?? [])?.reduce?.((p, c) => {
                        const res = { ...p };
                        const provider = c?.providerCode ?? 'main';
                        if (!res?.[provider]) {
                            res[provider] = {};
                        }
                        res[provider][c?.type] = c?.provided;
                        return res;
                    }, {});
                    const providersWithCards = getProfileCardsProviderCodes(getState?.(), profileId, consentsState?.profileId);
                    const consentList = filterClinicConsentsByCards(rawConsentList, providersWithCards);
                    const stateNow = getState?.()?.consents ?? {};
                    if (profileId === consentsState?.profileId) {
                        const mergedUser = mergeConsentProfileSlice(stateNow?.user ?? {}, consentList);
                        dispatch({type: TYPE, payload: { user: mergedUser, isLoading: false }});
                    } else {
                        const family = produce(stateNow?.family ?? {}, draft => {
                            const prev = draft[profileId] ?? {};
                            draft[profileId] = mergeConsentProfileSlice(prev, consentList);
                        });
                        dispatch({type: TYPE, payload: { family, isLoading: false }});
                    }
                } else {
                    dispatch({type: TYPE, payload: { isLoading: false }});
                }
                onComplete?.(res?.results);
                // console.log('Batch update consents result', res);
            }).catch(e => {
                console.log('Batch consent error',e);
            });
    } catch (e) {
        onError?.(e);
        dispatch({type: TYPE, payload: { isLoading: false }});
        console.log('Error batch', e);
    }
}

export const addConsentsToChildProfile = (profileId, consents, providerCode) => (dispatch, getState) => {
    const consentsState = getState?.()?.consents ?? { };
    const family = produce(consentsState?.family ?? {}, draft => {
        if (!draft?.[profileId]) {
            draft[profileId] = {};
        }
        if (Array.isArray(consents)) {
            consents.forEach(c => {
                if (c?.type && Object.hasOwn?.(c, 'provided')) {
                    const provider = c?.providerCode ?? 'main';
                    if (!draft[profileId]?.[provider]) {
                        draft[profileId][provider] = {};
                    }
                    draft[profileId][provider][c.type] = c.provided;
                }
            });
        } else if (typeof consents === 'object') {
            Object.entries(consents).forEach(([key, value]) => {
                const provider = providerCode ?? 'main';
                if (!draft[profileId]?.[provider]) {
                    draft[profileId][provider] = {};
                }
                draft[profileId][provider][key] = value;
            }
            );
        }
    });
    dispatch({type: TYPE, payload: { family }});
}

export const removeChildProfileFromConsents = profileId => (dispatch, getState) => {
    const consentsState = getState?.()?.consents ?? { };
    const family = produce(consentsState?.family ?? {}, draft => {
        delete draft[profileId];
    });
    dispatch({type: TYPE, payload: { family }});
}

export const setConsentFailedUpdateMode = failedUpdateMode => dispatch => dispatch({
    type: TYPE,
    payload: { failedUpdateMode },
});

export default createReducerFor(TYPE, initialState);
